"use client";

import { BriefcaseBusiness, ClipboardList, Headphones, Layers, MessageCircle, Users, Workflow } from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  DesktopSidebar,
  MobileSidebarDrawer,
  type ShellNavItem,
  type SidebarLabels,
} from "@/components/shell/admin-sidebar";
import { AdminTopbar, type TopbarLabels } from "@/components/shell/admin-topbar";
import { getProviderStatus } from "@/lib/api";
import { acquireBodyScrollLock } from "@/lib/body-scroll-lock";
import { useI18n } from "@/lib/i18n";
import { hasPermission } from "@/lib/permissions";
import { useAdminStore } from "@/store/admin-store";

const DESKTOP_BREAKPOINT_QUERY = "(min-width: 1024px)";
const DRAWER_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function AdminShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const hydrate = useAdminStore((state) => state.hydrate);
  const hydrated = useAdminStore((state) => state.hydrated);
  const user = useAdminStore((state) => state.user);
  const sessionToken = useAdminStore((state) => state.sessionToken);
  const searchQuery = useAdminStore((state) => state.searchQuery);
  const sidebarCollapsed = useAdminStore((state) => state.sidebarCollapsed);
  const setSearchQuery = useAdminStore((state) => state.setSearchQuery);
  const setSidebarCollapsed = useAdminStore((state) => state.setSidebarCollapsed);
  const signOut = useAdminStore((state) => state.signOut);
  const { locale, setLocale, t } = useI18n();
  const [providerReady, setProviderReady] = useState<boolean | null>(null);
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const mobileMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileDrawerRef = useRef<HTMLDivElement | null>(null);
  const mobileCloseButtonRef = useRef<HTMLButtonElement | null>(null);

  const navItems = useMemo<ShellNavItem[]>(
    () => [
      {
        href: "/usuarios",
        label: t.shell.users,
        icon: Users,
        visible: hasPermission(user?.permissions, "users:create") || hasPermission(user?.permissions, "users:update"),
      },
      {
        href: "/roles",
        label: t.shell.roles,
        icon: BriefcaseBusiness,
        visible: hasPermission(user?.permissions, "admin:access"),
      },
      {
        href: "/admin/generation",
        label: locale === "en" ? "Generation" : "Generación",
        icon: Workflow,
        visible: hasPermission(user?.permissions, "admin:access"),
      },
      {
        href: "/admin/reports",
        label: locale === "en" ? "Reports" : "Reportes",
        icon: Layers,
        visible: hasPermission(user?.permissions, "reports:read"),
      },
      {
        href: "/admin/podcasts",
        label: "Podcasts",
        icon: Headphones,
        visible: hasPermission(user?.permissions, "reports:read"),
      },
      {
        href: "/whatsapp",
        label: t.shell.whatsapp,
        icon: MessageCircle,
        visible: hasPermission(user?.permissions, "whatsapp:read"),
      },
      {
        href: "/registro",
        label: t.shell.usageLog,
        icon: ClipboardList,
        visible: hasPermission(user?.permissions, "whatsapp:read"),
      },
    ],
    [locale, t, user?.permissions],
  );
  const allowedNavItems = useMemo(() => navItems.filter((item) => item.visible), [navItems]);
  const sidebarLabels: SidebarLabels = {
    activeChannel: t.shell.activeChannel,
    ready: t.shell.ready,
    notReady: t.shell.notReady,
    navigationLabel: t.shell.navigationLabel,
    collapseSidebar: t.shell.collapseSidebar,
    expandSidebar: t.shell.expandSidebar,
    closeNavigation: t.shell.closeNavigation,
  };
  const topbarLabels: TopbarLabels = {
    searchPlaceholder: t.common.searchPlaceholder,
    notifications: t.shell.notifications,
    openNotifications: t.shell.openNotifications,
    noNotifications: t.shell.noNotifications,
    noNotificationsDescription: t.shell.noNotificationsDescription,
    openUserMenu: t.shell.openUserMenu,
    account: t.shell.account,
    signOut: t.shell.signOut,
    fallbackUserName: t.shell.fallbackUserName,
    openNavigation: t.shell.openNavigation,
  };

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (!hydrated) {
      return;
    }
    if (!sessionToken) {
      router.replace("/signin");
    }
  }, [hydrated, router, sessionToken]);

  useEffect(() => {
    if (!hydrated || !sessionToken || !pathname) {
      return;
    }
    const isAllowed = allowedNavItems.some((item) => pathname === item.href || pathname.startsWith(`${item.href}/`));
    if (isAllowed) {
      return;
    }
    const fallbackHref = allowedNavItems[0]?.href;
    if (fallbackHref && pathname !== fallbackHref) {
      router.replace(fallbackHref);
    }
  }, [allowedNavItems, hydrated, pathname, router, sessionToken]);

  useEffect(() => {
    if (!sessionToken || !hasPermission(user?.permissions, "admin:access")) {
      return;
    }

    let active = true;
    getProviderStatus()
      .then((payload) => {
        if (active) {
          setProviderReady(payload.ready);
        }
      })
      .catch(() => {
        if (active) {
          setProviderReady(null);
        }
      });

    return () => {
      active = false;
    };
  }, [sessionToken, user?.permissions]);

  useEffect(() => {
    if (!mobileNavigationOpen) {
      return;
    }

    const drawer = mobileDrawerRef.current;
    const menuButton = mobileMenuButtonRef.current;
    const desktopMediaQuery = window.matchMedia(DESKTOP_BREAKPOINT_QUERY);
    const releaseBodyScrollLock = acquireBodyScrollLock();

    mobileCloseButtonRef.current?.focus();

    const closeForDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setMobileNavigationOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (drawer?.closest('[aria-hidden="true"], [inert]')) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        setMobileNavigationOpen(false);
        return;
      }

      if (event.key !== "Tab" || !drawer) {
        return;
      }

      const focusableElements = Array.from(drawer.querySelectorAll<HTMLElement>(DRAWER_FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null,
      );
      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements.at(-1);

      if (!firstFocusable || !lastFocusable) {
        event.preventDefault();
        return;
      }

      if (event.shiftKey && (document.activeElement === firstFocusable || !drawer.contains(document.activeElement))) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === lastFocusable || !drawer.contains(document.activeElement))
      ) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    desktopMediaQuery.addEventListener("change", closeForDesktop);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      desktopMediaQuery.removeEventListener("change", closeForDesktop);
      releaseBodyScrollLock();
      if (menuButton?.isConnected && menuButton.offsetParent !== null) {
        menuButton.focus();
      }
    };
  }, [mobileNavigationOpen]);

  if (!hydrated || !sessionToken) {
    return <div className="min-h-screen" />;
  }

  return (
    <div className="flex min-h-screen bg-(--body-background) text-(--text)">
      <DesktopSidebar
        collapsed={sidebarCollapsed}
        onCollapsedChange={setSidebarCollapsed}
        panelId="admin-desktop-sidebar"
        navigationId="admin-desktop-navigation"
        navItems={allowedNavItems}
        pathname={pathname}
        providerReady={providerReady}
        labels={sidebarLabels}
      />

      <MobileSidebarDrawer
        open={mobileNavigationOpen}
        onClose={() => {
          setMobileNavigationOpen(false);
        }}
        drawerRef={mobileDrawerRef}
        closeButtonRef={mobileCloseButtonRef}
        panelId="admin-mobile-sidebar"
        navigationId="admin-mobile-navigation"
        navItems={allowedNavItems}
        pathname={pathname}
        providerReady={providerReady}
        labels={sidebarLabels}
      />

      <div className="min-w-0 flex-1">
        <AdminTopbar
          user={user}
          locale={locale}
          searchQuery={searchQuery}
          mobileNavigationOpen={mobileNavigationOpen}
          mobileMenuButtonRef={mobileMenuButtonRef}
          labels={topbarLabels}
          onLocaleChange={setLocale}
          onSearchQueryChange={setSearchQuery}
          onOpenMobileNavigation={() => {
            setMobileNavigationOpen(true);
          }}
          onSignOut={() => {
            signOut();
            router.replace("/signin");
          }}
        />

        <main className="px-4 py-5 sm:px-5 lg:px-8 lg:py-6">{children}</main>
      </div>
    </div>
  );
}
